import {
  Grid,
  IconButton,
  TextField,
  Button,
  Box,
  CircularProgress,
  Tooltip,
  Badge,
  Checkbox,
  FormControlLabel,
  Paper,
} from '@mui/material'
import PrimeTable from '../../../components/PrimeTable'
import {
  BsFileMedical,
  BsFiletypePdf,
  BsFloppy,
  BsPaperclip,
  BsTrash,
} from 'react-icons/bs'
import AutoComplete from '../../../components/AutoComplete/AutoComplete'
import { Formik } from 'formik'
import { Form, useNavigate } from 'react-router-dom'
import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { MdDelete } from 'react-icons/md'
import NumberSpinner from './qunatidadeProdutoTable'
import { currencyMask } from '../../../utils/masks'
import CurrencyField from '../../../components/CurrencyField/CurrencyField'
import { barraInicial, PontoDeVendaContext } from '.'
import { GlobalContext } from '../../../App'
import { metodoPrograma } from '../../../components/api'
import {
  setSuccess,
  setError,
  setInfo,
  setWarning,
} from '../../../components/alerts'

import * as Yup from 'yup'
import ModalAnexaComprovante from './modal/modalAnexarComprovante'
import { useModal } from '../../../components/Utils/UseModal'
import { StatusChip } from './lista'
import BotaoEstornos from '../components/BotaoEstornos'
import ModalImprime from './modal/modalImprime'

const validationSchema = Yup.object().shape({
  acCliente: Yup.array()
    .nullable()
    .min(1, 'Selecione um cliente')
    .required('Campo obrigatório'),

  acFormaPagamento: Yup.array()
    .nullable()
    .when('snCobraCheckout', {
      is: (val) => val !== 'S',
      then: (schema) =>
        schema
          .min(1, 'Selecione a forma de pagamento')
          .required('Campo obrigatório'),
      otherwise: (schema) => schema.notRequired(),
    }),

  formasPagamento: Yup.array().when('snCobraCheckout', {
    is: (val) => val !== 'S',
    then: (schema) =>
      schema
        .of(
          Yup.object({
            vlPago: Yup.number()
              .moreThan(0, 'O valor deve ser maior que 0')
              .required('valor é obrigatório')
              .typeError('valor deve ser um número'),
            qtParcelas: Yup.number().when('isParcelas', {
              is: true,
              then: (s) =>
                s
                  .moreThan(0, 'A quantidade de parcelas deve ser maior que 0')
                  .required('Quantidade de parcelas é obrigatória')
                  .typeError('Quantidade de parcelas deve ser um número'),
              otherwise: (s) => s.notRequired(),
            }),
          }),
        )
        .test(
          'formasPagamento-valido',
          'Cada item deve conter vlPago válido',
          function (value) {
            if (!value || value.length === 0) return true

            const { isParcelas } = this.options.context

            const itemIndex = value.findIndex(
              (item) => item.vlPago === undefined || item.vlPago === null,
            )

            if (itemIndex !== -1) {
              return this.createError({
                path: `formasPagamento[${itemIndex}].vlPago`,
                message: `Item ${itemIndex + 1}: Valor é obrigatório`,
              })
            }

            if (isParcelas) {
              const parcelaIndex = value.findIndex(
                (item) =>
                  item.qtParcelas === undefined ||
                  item.qtParcelas === null ||
                  item.qtParcelas <= 0,
              )

              if (parcelaIndex !== -1) {
                return this.createError({
                  path: `formasPagamento[${parcelaIndex}].qtParcelas`,
                  message: `Item ${parcelaIndex + 1}: Quantidade de parcelas é obrigatória`,
                })
              }
            }

            return true
          },
        ),
    otherwise: (schema) => schema.notRequired(),
  }),

  vlTotal: Yup.number().min(0, 'O total não pode ser negativo'),
})

const MntPontoDeVenda = () => {
  const { cadastroVenda, dispatch, idPontoVenda } =
    useContext(PontoDeVendaContext)

  const { listaProduto } = cadastroVenda
  const modalAnexarComprovante = useModal(ModalAnexaComprovante)
  const modalImprime = useModal(ModalImprime)

  const navigate = useNavigate()
  const { localAtendimento, usuarioLogado } = useContext(GlobalContext)
  const [textFieldAc, setTextFieldAc] = useState('')
  const [bloqueia, setBloqueia] = useState({ cliente: false })
  const [qtdAnexos, setQtdAnexos] = useState(0)
  const [situacaoInicial, setSituacaoInicial] = useState(
    cadastroVenda?.tpSituacao,
  )

  const idLocalAtendimento = useMemo(
    () => ({ idLocalAtendimento: localAtendimento?.[0] }),
    [localAtendimento],
  )

  const valorTotalTabela = useMemo(
    () =>
      listaProduto?.reduce(
        (acumulador, produto) => acumulador + Number(produto?.vlTotal || 0),
        0,
      ) || 0,
    [listaProduto],
  )

  const calculaTotalPagar = (vlDesconto) => {
    const desconto = Number(vlDesconto)

    return Math.max(
      0,
      valorTotalTabela - (Number.isFinite(desconto) ? desconto : 0),
    )
  }

  const pagamentoCorrespondeAoTotal = (values) => {
    if (values?.snCobraCheckout === 'S') return true

    const valorPago = (values?.formasPagamento || []).reduce(
      (acumulador, formaPagamento) =>
        acumulador + Number(formaPagamento?.vlPago || 0),
      0,
    )
    const totalPagar = calculaTotalPagar(values?.vlDesconto)

    return Math.abs(valorPago - totalPagar) < 0.01
  }

  const insereProdutoTabela = (e, produto) => {
    setTextFieldAc('')

    if (!produto?.[0]?.[0]) return

    const idx = Symbol('idx ' + produto?.[1])

    const produtoTratado = {
      idx,
      idProduto: produto?.[0]?.[0],
      tpProduto: produto?.[0]?.[1],
      dsProduto: produto?.[1],
      qtProduto: 1,
      vlUnitario: produto?.[2],
      vlUnitarioInicial: produto?.[2],
      vlTotal: produto?.[2],
      qtEstoque: produto?.[3] || 0,
    }

    const listaTratada = [...listaProduto, produtoTratado]

    dispatch({
      type: 'cadastroVenda',
      field: 'listaProduto',
      value: listaTratada,
    })
  }

  const excluiItem = async (value) => {
    dispatch({
      type: 'cadastroVenda',
      field: 'listaProduto',
      value: listaProduto.filter((item) => item?.idx !== value?.idx),
    })
  }

  const trataQuantidadeLinha = (linha) => (quantidade) => {
    const listaAlterada = listaProduto.map((v) =>
      v.idx === linha.idx
        ? { ...v, vlTotal: v.vlUnitario * quantidade, qtProduto: quantidade }
        : v,
    )

    dispatch({
      type: 'cadastroVenda',
      field: 'listaProduto',
      value: listaAlterada,
    })
  }

  const trataValorLinha = (linha) => (unitario) => {
    const listaAlterada = listaProduto.map((v) =>
      v.idx === linha.idx
        ? {
            ...v,
            vlTotal: v.qtProduto !== 0 ? unitario * v.qtProduto : 0,
            vlUnitario: unitario,
          }
        : v,
    )

    dispatch({
      type: 'cadastroVenda',
      field: 'listaProduto',
      value: listaAlterada,
    })
  }

  const anexarComprovante = (idPedidoVenda) => async () => {
    await modalAnexarComprovante({ idPedidoVenda })
  }

  const salvarVenda =
    (values, validateForm, setTouched, setFieldError, setSubmitting) =>
    async () => {
      setSubmitting(true)
      const errors = await validateForm()

      if (Object.keys(errors).length > 0) {
        const touchedFields = Object.keys(errors).reduce((acc, key) => {
          acc[key] = true
          return acc
        }, {})
        setTouched(touchedFields)

        Object.keys(errors).forEach((key) => {
          setFieldError(key, errors[key])
        })

        setError(
          'Por favor, preencha todos os campos obrigatórios corretamente.',
        )
        setSubmitting(false)
        return null
      }

      if (!pagamentoCorrespondeAoTotal(values)) {
        setSubmitting(false)
        return setInfo('O valor pago deve ser igual ao Total a Pagar')
      }

      const valorZerado = values?.formasPagamento?.find((v) => v?.vlPago === 0)

      if (valorZerado)
        setWarning('A proposta não pode conter valor de pagamento zerado')

      const payload = {
        vlDesconto: values?.vlDesconto,
        vlTotal: calculaTotalPagar(values?.vlDesconto),
        prDesconto: values?.prDesconto,
        itens: listaProduto,
        idAtendimento: values?.acAtendimento?.[0]?.[0],
        idPaciente: values?.acCliente?.[0]?.[0],
        dsPaciente: values?.acCliente?.[1],
        nrCpf: values?.acCliente?.[3],
        tpSituacao: values?.tpSituacao,
        idFormaPagamento: values?.acFormaPagamento?.[0]?.[0],
        idPontoVenda,
        idPedidoVenda: values?.idPedidoVenda ? values?.idPedidoVenda : null,
        formasPagamento: values?.formasPagamento,
        snCobraCheckout: values?.snCobraCheckout,
        dsObservacoes: values?.dsObservacoes,
      }

      const { status, pedidoVenda } = await metodoPrograma(
        'salvaPedido',
        payload,
      )
      if (status) {
        dispatch({
          type: 'cadastroVenda',
          field: 'idPedidoVenda',
          value: pedidoVenda?.idPedidoVenda || pedidoVenda,
        })
        setSuccess('Venda Salva!')
        setSubmitting(false)
        setSituacaoInicial(pedidoVenda?.tpSituacao || '0')
        return pedidoVenda?.idPedidoVenda || pedidoVenda
      }
      return null
    }
  const prescreverVenda = (values) => () => {
    const acCliente = values?.acCliente
    const dsConteudo = listaProduto
      .map(
        (item, idx) =>
          `${idx + 1}) ${item?.dsProduto || item?.dsItem || item?.dsTerapia}`,
      )
      .join('\n\n\n')

    navigate('/programas/consultorio/receita_avulsa', {
      state: { acCliente, dsConteudo },
    })
  }
  const emitirVenda = async (
    values,
    validateForm,
    setTouched,
    setFieldError,
    setSubmitting,
  ) => {
    let idPedidoVenda = values?.idPedidoVenda

    idPedidoVenda = await salvarVenda(
      values,
      validateForm,
      setTouched,
      setFieldError,
      setSubmitting,
    )()
    if (!idPedidoVenda) return

    const { status } = await metodoPrograma('emitePedido', {
      idPedidoVenda,
      formasPagamento: values?.formasPagamento,
      snCobraCheckout: values?.snCobraCheckout === 'S',
    })

    if (status) {
      setSuccess('Venda Confirmada')
      setSituacaoInicial('1')
    }
  }

  const excluirPedido = (idPedidoVenda, setSubmitting) => async () => {
    setSubmitting(true)
    const { status } = await metodoPrograma('excluiPedido', { idPedidoVenda })
    if (status) {
      setSuccess('Pedido Excluido!')
      dispatch({ type: 'limpaCadastroVenda' })
      dispatch({ type: 'barraFerramenta', value: barraInicial })
      dispatch({ type: 'tela', value: 'lista' })
    }
    setSubmitting(false)
  }

  const alteraCampos = (field, value) =>
    dispatch({
      type: 'cadastroVenda',
      field,
      value,
    })

  const getQtdAnexos = useCallback(async () => {
    if (!cadastroVenda?.idPedidoVenda) return setQtdAnexos(0)

    const { qtd } = await metodoPrograma('getQtdAnexosPedido', {
      idUsuario: usuarioLogado.id,
      idPedidoVenda: cadastroVenda?.idPedidoVenda,
    })
    setQtdAnexos(qtd || 0)
  }, [cadastroVenda?.idPedidoVenda, usuarioLogado.id])

  useEffect(() => {
    getQtdAnexos()
  }, [getQtdAnexos])

  return (
    <Formik
      initialValues={cadastroVenda}
      validationSchema={validationSchema}
      enableReinitialize
      onSubmit={async (
        values,
        { setSubmitting, validateForm, setTouched, setFieldError },
      ) => {
        if (!pagamentoCorrespondeAoTotal(values))
          return setInfo('O valor pago deve ser igual ao Total a Pagar')
        await emitirVenda(
          values,
          validateForm,
          setTouched,
          setFieldError,
          setSubmitting,
        )
        setSubmitting(false)
      }}
    >
      {({
        values,
        submitForm,
        errors,
        touched,
        validateForm,
        setTouched,
        setFieldError,
        isSubmitting,
        setSubmitting,
      }) => (
        <Form style={{ width: '100%' }}>
          <Grid container spacing={2}>
            <Grid item xs={8}>
              <Paper
                variant="outlined"
                style={{
                  borderRadius: 16,
                  padding: 16,
                  backgroundColor: 'rgba(0, 0, 0, 0.03)',
                  height: '100%',
                }}
              >
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <AutoComplete
                      fullWidth
                      variant="outlined"
                      margin="dense"
                      InputLabelProps={{ shrink: true }}
                      label="Adicionar Produto"
                      metodo="buscaProdutoTerapiaPontoVenda"
                      disabled={isSubmitting || situacaoInicial !== '0'}
                      value={values.acProduto}
                      textFieldProps={{
                        onChange: (v) => setTextFieldAc(v.target.value),
                      }}
                      onChange={insereProdutoTabela}
                      inputValueAc={textFieldAc}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <PrimeTable
                      dadosTable={listaProduto}
                      disablePagination
                      colStyle={[
                        { width: '50%' },
                        { width: '10%' },
                        { width: '15%' },
                        { width: '15%' },
                        { width: '1%' },
                      ]}
                      head={[
                        'Produto',
                        'Quantidade',
                        'Valor Unitario',
                        'Valor Total',
                        '',
                      ]}
                      line={(item) => [
                        item.dsProduto,
                        0,
                        item.vlUnitario,
                        item.vlTotal,
                        '',
                      ]}
                      columnOptions={[
                        {
                          alias: 0,
                          customJsx: ({ linhaOriginal }) => (
                            <Grid container>
                              <Grid item xs={12}>
                                <Tooltip title={linhaOriginal.dsProduto}>
                                  <span
                                    style={{
                                      fontSize: '1.5vh',
                                      fontWeight: 370,
                                      textOverflow: 'ellipsis',
                                      overflow: 'hidden',
                                      display: 'block',
                                    }}
                                  >
                                    {linhaOriginal.dsProduto}
                                  </span>
                                </Tooltip>
                              </Grid>
                              <Grid item xs={12}>
                                <span style={{ fontSize: '1vh' }}>
                                  {`Unitário R$: ${currencyMask(linhaOriginal.vlUnitarioInicial)}`}
                                </span>
                              </Grid>
                              <Grid item xs={12}>
                                <span style={{ fontSize: '1vh' }}>
                                  {`Estoque: ${linhaOriginal.qtEstoque}`}
                                </span>
                              </Grid>
                            </Grid>
                          ),
                        },
                        {
                          alias: 1,
                          customJsx: ({ linhaOriginal }) => (
                            <Box
                              style={{
                                display: 'flex',
                                justifyContent: 'flex-start',
                              }}
                            >
                              <NumberSpinner
                                size="small"
                                disabled={
                                  isSubmitting || situacaoInicial !== '0'
                                }
                                onChange={trataQuantidadeLinha(linhaOriginal)}
                                value={linhaOriginal?.qtProduto}
                                min={1}
                              />
                            </Box>
                          ),
                        },

                        {
                          alias: 2,
                          customJsx: ({ linhaOriginal }) => (
                            <CurrencyField
                              fullWidth
                              variant="outlined"
                              margin="dense"
                              size="small"
                              disabled={isSubmitting || situacaoInicial !== '0'}
                              value={linhaOriginal.vlUnitario}
                              onChange={trataValorLinha(linhaOriginal)}
                            />
                          ),
                        },
                        {
                          alias: 3,
                          customJsx: ({ linhaOriginal }) => (
                            <CurrencyField
                              fullWidth
                              variant="outlined"
                              margin="dense"
                              size="small"
                              disabled
                              value={linhaOriginal.vlTotal}
                            />
                          ),
                        },
                        {
                          alias: 4,
                          customJsx: ({ linhaOriginal }) => (
                            <Box
                              style={{
                                display: 'flex',
                                justifyContent: 'flex-end',
                              }}
                            >
                              <IconButton
                                size="small"
                                disabled={
                                  isSubmitting || situacaoInicial !== '0'
                                }
                                onClick={() => excluiItem(linhaOriginal)}
                              >
                                <MdDelete />
                              </IconButton>
                            </Box>
                          ),
                        },
                      ]}
                    />
                  </Grid>
                </Grid>
              </Paper>
            </Grid>

            <Grid item xs={4}>
              <Paper
                variant="outlined"
                style={{
                  borderRadius: 16,
                  padding: 16,
                  backgroundColor: 'rgba(0, 0, 0, 0.03)',
                  position: 'sticky',
                  top: 60,
                }}
              >
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <AutoComplete
                      InputLabelProps={{ shrink: true }}
                      label="Atendimento"
                      metodo="buscaAtendimentoPontoVenda"
                      getOptionLabel={(item) =>
                        Array.isArray(item) && item.length > 0
                          ? `Proposta ${item?.[0]?.[0] ?? '-'} - ${item?.[1] ?? ''}`
                          : ''
                      }
                      isOptionEqualToValue={(option, value) =>
                        option?.[0]?.[0] === value?.[0]?.[0]
                      }
                      disabled={isSubmitting || situacaoInicial !== '0'}
                      value={values.acAtendimento}
                      onChange={(_, val) => {
                        alteraCampos('acCliente', val?.[5])
                        alteraCampos('acAtendimento', val)
                        setBloqueia((v) => ({ ...v, cliente: !!val?.[5] }))
                      }}
                      textFieldProps={{
                        error: touched.acAtendimento && !!errors.acAtendimento,
                        helperText:
                          touched.acAtendimento && errors.acAtendimento,
                      }}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <AutoComplete
                      InputLabelProps={{ shrink: true }}
                      label="Cliente"
                      codigo="CtrlPontoVenda"
                      metodo="buscaPaciente"
                      searchParams={idLocalAtendimento}
                      disabled={
                        bloqueia?.cliente ||
                        isSubmitting ||
                        situacaoInicial !== '0'
                      }
                      value={values.acCliente || null}
                      onChange={(_, val) => {
                        alteraCampos('acCliente', val)
                      }}
                      textFieldProps={{
                        error: touched.acCliente && !!errors.acCliente,
                        helperText: touched.acCliente && errors.acCliente,
                      }}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <AutoComplete
                      InputLabelProps={{ shrink: true }}
                      label="Forma de Pagamento"
                      metodo="buscaFormaPagamento"
                      multiple
                      filterFunction={() => true}
                      filterSelectedOptions={false}
                      value={values?.acFormaPagamento ?? []}
                      disabled={isSubmitting || situacaoInicial !== '0'}
                      onChange={(_, val) => {
                        const opcoesSelecionadas = val?.map((item) => {
                          const idFormaPagamento = item?.[0]?.[0]
                          const unic = item?.[0]?.[1] || crypto.randomUUID()

                          return [[idFormaPagamento, unic], ...item.slice(1)]
                        })
                        let valorJaDistribuido = 0
                        const totalPagar = calculaTotalPagar(values?.vlDesconto)
                        const valorTratado = opcoesSelecionadas?.map((item) => {
                          const idFormaPagamento = item?.[0]?.[0]
                          const unic = item?.[0]?.[1]
                          const existente = values?.formasPagamento?.find(
                            (v) =>
                              v.idFormaPagamento === idFormaPagamento &&
                              v.unic === unic,
                          )
                          const vlPago = existente
                            ? Number(existente.vlPago || 0)
                            : Math.max(0, totalPagar - valorJaDistribuido)

                          valorJaDistribuido += vlPago

                          return {
                            idFormaPagamento,
                            vlPago,
                            qtParcelas: existente
                              ? existente.qtParcelas
                              : item?.[4],
                            isParcelas: existente
                              ? existente.isParcelas
                              : item?.[3],
                            unic,
                          }
                        })

                        alteraCampos('formasPagamento', valorTratado)
                        alteraCampos('acFormaPagamento', opcoesSelecionadas)
                      }}
                      textFieldProps={{
                        error:
                          touched.acFormaPagamento && !!errors.acFormaPagamento,
                        helperText:
                          touched.acFormaPagamento && errors.acFormaPagamento,
                      }}
                    />
                  </Grid>
                  {values?.acFormaPagamento?.map((item, index) => (
                    <Grid
                      item
                      key={item?.[0]?.[1] || index}
                      container
                      spacing={2}
                      xs={12}
                    >
                      <Grid item xs={item?.[3] ? 6 : 12}>
                        <CurrencyField
                          fullWidth
                          variant="outlined"
                          margin="dense"
                          label={item?.[1]}
                          disabled={isSubmitting || situacaoInicial !== '0'}
                          size="small"
                          value={
                            values?.formasPagamento?.find(
                              (v) => v.unic === item?.[0]?.[1],
                            )?.vlPago || 0
                          }
                          onChange={(val) => {
                            alteraCampos(
                              'formasPagamento',
                              values?.formasPagamento?.map((v) => {
                                return v?.unic === item?.[0]?.[1]
                                  ? { ...v, vlPago: val }
                                  : v
                              }),
                            )
                          }}
                          helperText={
                            errors?.formasPagamento?.[index]?.vlPago
                              ? errors?.formasPagamento?.[index]?.vlPago
                              : ''
                          }
                          error={!!errors?.formasPagamento?.[index]?.vlPago}
                        />
                      </Grid>
                      {item?.[3] && (
                        <Grid item xs={6}>
                          <TextField
                            fullWidth
                            variant="outlined"
                            size="small"
                            type="number"
                            margin="dense"
                            label="Parcelas"
                            InputLabelProps={{ shrink: true }}
                            disabled={isSubmitting || situacaoInicial !== '0'}
                            value={
                              values?.formasPagamento?.find(
                                (v) => v.unic === item?.[0]?.[1],
                              )?.qtParcelas
                            }
                            onChange={(e) => {
                              const val = e?.target?.value
                              alteraCampos(
                                'formasPagamento',
                                values?.formasPagamento?.map((v) => {
                                  return v?.unic === item?.[0]?.[1]
                                    ? {
                                        ...v,
                                        qtParcelas: val ? Number(val) : '',
                                      }
                                    : v
                                }),
                              )
                            }}
                            helperText={
                              errors?.formasPagamento?.[index]?.qtParcelas
                                ? errors?.formasPagamento?.[index]?.qtParcelas
                                : ''
                            }
                            error={
                              !!errors?.formasPagamento?.[index]?.qtParcelas
                            }
                          />
                        </Grid>
                      )}
                    </Grid>
                  ))}
                  <Grid item xs={12}>
                    <Button
                      fullWidth
                      variant="outlined"
                      startIcon={<BsFiletypePdf />}
                      disabled={isSubmitting || !listaProduto?.length}
                      onClick={() =>
                        modalImprime({
                          idPedidoVenda: values.idPedidoVenda,
                          nomePaciente: values.acCliente?.[1],
                          idAtendimento: values.acAtendimento?.[0]?.[0],
                          statusPedido: situacaoInicial,
                          snCobraCheckout: values.snCobraCheckout,
                          itens: listaProduto,
                          desconto: values.vlDesconto,
                          dsObservacoes: values.dsObservacoes,
                          formasPagamento: (values.acFormaPagamento || []).map(
                            (forma) => {
                              const pagamento = values.formasPagamento?.find(
                                (item) => item.unic === forma?.[0]?.[1],
                              )
                              return {
                                nome: forma?.[1],
                                vlPago: pagamento?.vlPago || 0,
                                qtParcelas: forma?.[3]
                                  ? pagamento?.qtParcelas
                                  : 0,
                              }
                            },
                          ),
                          emissaoData: new Date().toLocaleDateString('pt-BR', {
                            day: 'numeric',
                            month: 'long',
                            year: 'numeric',
                          }),
                        })
                      }
                    >
                      Imprimir PDF
                    </Button>
                  </Grid>
                  <Grid item xs={12}>
                    <Box style={{ flexGrow: 1, minHeight: 40 }} />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      variant="outlined"
                      margin="dense"
                      label="Porcentagem (%)"
                      size="small"
                      type="number"
                      value={values?.prDesconto}
                      disabled={isSubmitting || situacaoInicial !== '0'}
                      onChange={(e) => {
                        const pr = parseFloat(e.target.value) || 0

                        const vlCalculado = (pr / 100) * valorTotalTabela

                        alteraCampos('prDesconto', pr || 0)
                        alteraCampos('vlDesconto', vlCalculado)
                        alteraCampos('vlTotal', calculaTotalPagar(vlCalculado))
                      }}
                      onFocus={(e) =>
                        e.target.value === '0' && alteraCampos('prDesconto', '')
                      }
                      error={touched.prDesconto && !!errors.prDesconto}
                      helperText={touched.prDesconto && errors.prDesconto}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <CurrencyField
                      fullWidth
                      variant="outlined"
                      margin="dense"
                      label="Desconto (R$)"
                      disabled={isSubmitting || situacaoInicial !== '0'}
                      size="small"
                      value={values?.vlDesconto || 0}
                      onChange={(v) => {
                        const vl = v || 0
                        const prCalculado =
                          valorTotalTabela > 0
                            ? (vl / valorTotalTabela) * 100
                            : 0

                        alteraCampos('vlDesconto', vl)
                        alteraCampos('prDesconto', prCalculado.toFixed(2))
                        alteraCampos('vlTotal', calculaTotalPagar(vl))
                      }}
                      helperText={
                        touched.vlDesconto && errors.vlDesconto
                          ? errors.vlDesconto
                          : ''
                      }
                      error={touched.vlDesconto && !!errors.vlDesconto}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      variant="outlined"
                      margin="dense"
                      label="Sub Total"
                      size="small"
                      disabled
                      value={`R$ ${currencyMask(valorTotalTabela)}`}
                      autoFocus
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      variant="outlined"
                      margin="dense"
                      label="Total a Pagar"
                      size="small"
                      autoFocus
                      disabled
                      value={`R$ ${currencyMask(
                        calculaTotalPagar(values?.vlDesconto),
                      )}`}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <FormControlLabel
                      disabled={
                        isSubmitting ||
                        situacaoInicial !== '0' ||
                        !values?.acAtendimento?.[0]?.[0]
                      }
                      control={
                        <Checkbox
                          color="primary"
                          checked={values.snCobraCheckout === 'S'}
                          onClick={(e) =>
                            alteraCampos(
                              'snCobraCheckout',
                              e.target?.checked ? 'S' : 'N',
                            )
                          }
                        />
                      }
                      label="Cobrar no Checkout"
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <StatusChip
                      style={{ width: '100%' }}
                      status={values?.tpSituacao}
                    />
                    <BotaoEstornos
                      codigo="CtrlPontoVenda"
                      idPedidoVenda={values?.idPedidoVenda}
                      habilitado={values?.tpSituacao === '2'}
                      temPagamento={(values?.formasPagamento || []).some(
                        (forma) => Number(forma?.vlPago || 0) > 0,
                      )}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="Observação"
                      fullWidth
                      size="small"
                      multiline
                      rows={3}
                      disabled={isSubmitting || situacaoInicial !== '0'}
                      value={values?.dsObservacoes || ''}
                      onChange={(e) =>
                        alteraCampos('dsObservacoes', e.target.value)
                      }
                    />
                  </Grid>

                  <Grid item container xs={4}>
                    <Grid item xs={4}>
                      <IconButton
                        disabled={
                          !values?.idPedidoVenda ||
                          isSubmitting ||
                          situacaoInicial !== '0'
                        }
                        title="Excluir"
                        onClick={excluirPedido(
                          values?.idPedidoVenda,
                          setSubmitting,
                        )}
                      >
                        <BsTrash />
                      </IconButton>
                    </Grid>
                    <Grid item xs={4}>
                      <IconButton
                        title="Salvar"
                        disabled={isSubmitting || situacaoInicial !== '0'}
                        onClick={salvarVenda(
                          values,
                          validateForm,
                          setTouched,
                          setFieldError,
                          setSubmitting,
                        )}
                      >
                        <BsFloppy />
                      </IconButton>
                    </Grid>
                    <Grid item xs={4}>
                      <IconButton
                        title="Anexar Comprovante"
                        disabled={
                          !values?.idPedidoVenda ||
                          isSubmitting ||
                          (values?.tpSituacao !== '0' &&
                            situacaoInicial !== '1')
                        }
                        onClick={anexarComprovante(values?.idPedidoVenda)}
                      >
                        <Badge badgeContent={qtdAnexos} color="primary">
                          <BsPaperclip />
                        </Badge>
                      </IconButton>
                    </Grid>
                    <Grid item xs={4}>
                      <IconButton
                        title="Prescrever"
                        disabled={isSubmitting || situacaoInicial === '0'}
                        onClick={prescreverVenda(values)}
                      >
                        <BsFileMedical />
                      </IconButton>
                    </Grid>
                  </Grid>
                  <Grid
                    item
                    style={{
                      justifyContent: 'flex-end',
                      alignContent: 'center',
                    }}
                    xs={8}
                  >
                    <Button
                      fullWidth
                      variant="contained"
                      color="primary"
                      size="small"
                      disabled={isSubmitting || situacaoInicial !== '0'}
                      endIcon={
                        isSubmitting ? (
                          <CircularProgress color="inherit" size={15} />
                        ) : null
                      }
                      onClick={submitForm}
                    >
                      Confirmar Pedido
                    </Button>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
          </Grid>
        </Form>
      )}
    </Formik>
  )
}

export default MntPontoDeVenda
